/**
 * KI-Prüfung in EINEM Kasten (Matze 09.10.2026): Bilder, Eigenschaften und Texte für die
 * angehakten Artikel — live, was offen ist und was es kostet, ein Knopf, ein Job.
 * „Sonst muss ich ja drei Mal klicken und drei Mal warten. Das ist irgendwie Quatsch."
 *
 * Reihenfolge im Job: Bilder → Eigenschaften (Gemini bekommt das Hauptbild) → Texte.
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';
import { usd, zahl } from '../lib/format';
import type { KiPruefungUebersicht, KiTeil } from '../lib/typen';

interface Props {
  entwurfId: number;
  /** Ändert sich mit jedem Häkchen — dann wird neu gezählt. */
  stand: string;
  aufFertig: () => void;
}

export default function KiPruefungKasten({ entwurfId, stand, aufFertig }: Props) {
  const [u, setU] = useState<KiPruefungUebersicht | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<Record<KiTeil, boolean>>({ bilder: true, eigenschaften: true, texte: true });
  const [neu, setNeu] = useState(false);
  const [neuLaden, setNeuLaden] = useState(0);
  const job = useJobStatus(null);

  useEffect(() => {
    let abgebrochen = false;
    setLaedt(true);
    api.get<{ data: KiPruefungUebersicht }>(`/programmes/artikelimport/entwuerfe/${entwurfId}/ki-pruefung`)
      .then((a: { data: { data: KiPruefungUebersicht } }) => { if (!abgebrochen) setU(a.data.data); })
      .catch(() => { if (!abgebrochen) setU(null); })
      .finally(() => { if (!abgebrochen) setLaedt(false); });
    return () => { abgebrochen = true; };
  }, [entwurfId, stand, neuLaden]);

  useEffect(() => {
    if (job.status === 'completed' || job.status === 'failed') {
      setNeuLaden(n => n + 1);
      aufFertig();
    }
    // aufFertig bewusst nicht in den Abhängigkeiten — nur auf den Statuswechsel reagieren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.status]);

  if (!u || u.artikel === 0) return null;

  // Was der Knopf jetzt anstoßen würde — gewählt UND offen (mit „neu" auch Geprüftes).
  const texteZahl = gewaehlt.texte && u.texte.aktiv ? (neu ? u.texte.gesamt : u.texte.offen) : 0;
  const eigenschaftenZahl = gewaehlt.eigenschaften ? (neu ? u.eigenschaften.gesamt : u.eigenschaften.offen) : 0;
  const farbenZahl = gewaehlt.bilder ? u.bilder.farbenOffen : 0;
  const bilderZahl = gewaehlt.bilder ? u.bilder.bilderOffen : 0;
  const jeText = u.texte.jeArtikelUsd;
  const jeEigenschaft = u.eigenschaften.jeArtikelUsd;
  const kosten = texteZahl * jeText + eigenschaftenZahl * jeEigenschaft + (gewaehlt.bilder ? u.bilder.schaetzungUsd : 0);
  const nichtsZuTun = texteZahl + eigenschaftenZahl + farbenZahl === 0;

  const starten = async () => {
    setFehler(null);
    try {
      await job.startJob(`/programmes/artikelimport/entwuerfe/${entwurfId}/ki-pruefung`, { teile: gewaehlt, neu });
    } catch (err: any) {
      setFehler(err?.response?.status === 409 ? 'Läuft bereits.' : (err?.response?.data?.error?.message ?? 'Start fehlgeschlagen.'));
    }
  };

  const zeile = (
    teil: KiTeil, titel: string, zustand: string, offen: string | null, kostenText: string, hinweis?: string | null, aus?: boolean,
  ) => (
    <label className={`flex items-start gap-3 py-1.5 ${aus ? 'opacity-50' : ''}`}>
      <input
        type="checkbox" className="mt-1" checked={gewaehlt[teil] && !aus} disabled={aus || job.isRunning}
        onChange={e => setGewaehlt(g => ({ ...g, [teil]: e.target.checked }))}
      />
      <span className="flex-1">
        <b>{titel}</b> <span className="text-[#6B6A66]">{zustand}</span>
        {offen && <span className="ml-2 rounded bg-[#F0F0EC] px-2 py-0.5 text-xs">{offen}</span>}
        {hinweis && <span className="block text-xs text-amber-900">{hinweis}</span>}
      </span>
      <span className="tabular-nums text-xs text-[#6B6A66]">{kostenText}</span>
    </label>
  );

  return (
    <div className="rounded-lg border border-[#DAD9D9] bg-white px-4 py-3 text-sm">
      <div className="mb-1 flex items-center justify-between">
        <span className="font-semibold text-[#1A1A1A]">KI-Prüfung · {zahl(u.artikel)} angehakte Artikel</span>
        {laedt && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#6B6A66]" />}
      </div>

      <div className="divide-y divide-[#F0F0EC]">
        {zeile(
          'bilder', 'Bilder',
          `${zahl(u.bilder.farben - u.bilder.farbenOffen)} von ${zahl(u.bilder.farben)} Farben geprüft`,
          u.bilder.farbenOffen > 0 ? `${zahl(u.bilder.farbenOffen)} Farben · ${zahl(u.bilder.bilderOffen)} Bilder offen` : null,
          u.bilder.farbenOffen > 0 ? `≈ ${usd(u.bilder.schaetzungUsd)}` : 'fertig',
          u.bilder.farbenOffen > 0 ? 'Ungeprüft wählt das Tool Haupt- und Rückbild nach dem Dateinamen.' : null,
          u.bilder.farben === 0,
        )}
        {zeile(
          'eigenschaften', 'Eigenschaften',
          `${zahl(u.eigenschaften.gesamt - u.eigenschaften.offen)} von ${zahl(u.eigenschaften.gesamt)} geprüft`
            + (u.eigenschaften.abweichungen > 0 ? ` · ${zahl(u.eigenschaften.abweichungen)} anders als Regel` : '')
            + (u.eigenschaften.kostenBisherUsd > 0 ? ` · bisher ${usd(u.eigenschaften.kostenBisherUsd)}` : ''),
          eigenschaftenZahl > 0 ? `${zahl(eigenschaftenZahl)} Artikel` : null,
          eigenschaftenZahl > 0 ? `≈ ${usd(eigenschaftenZahl * jeEigenschaft)}` : 'fertig',
          u.eigenschaften.offen > 0 ? 'Ungeprüft bekommen sie beim Anlegen nur die Regelwerte.' : null,
        )}
        {zeile(
          'texte', 'Texte',
          u.texte.aktiv
            ? `${zahl(u.texte.gesamt - u.texte.offen)} von ${zahl(u.texte.gesamt)} fertig`
              + (u.texte.veraltet > 0 ? ` · ${zahl(u.texte.veraltet)} veraltet` : '')
            : 'für diesen Hersteller ausgeschaltet',
          texteZahl > 0 ? `${zahl(texteZahl)} Texte` : null,
          texteZahl > 0 ? `≈ ${usd(texteZahl * jeText)}` : (u.texte.aktiv ? 'fertig' : '—'),
          null,
          !u.texte.aktiv,
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void starten()}
          disabled={job.isRunning || nichtsZuTun}
          className="flex items-center gap-2 rounded bg-[#E9FF57] px-3 py-1.5 text-sm font-semibold text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-60"
        >
          {job.isRunning && <Loader2 className="h-4 w-4 animate-spin" />}
          {job.isRunning
            ? `Gemini prüft … ${job.progress ? `${zahl(job.progress.percentage)} %` : ''}`
            : nichtsZuTun
              ? 'Alles geprüft'
              : `Prüfen: ${[
                farbenZahl > 0 ? `${zahl(bilderZahl)} Bilder` : null,
                eigenschaftenZahl > 0 ? `${zahl(eigenschaftenZahl)} Eigenschaften` : null,
                texteZahl > 0 ? `${zahl(texteZahl)} Texte` : null,
              ].filter(Boolean).join(' · ')} (≈ ${usd(kosten)})`}
        </button>
        <label className="flex items-center gap-1.5 text-xs text-[#6B6A66]">
          <input type="checkbox" checked={neu} disabled={job.isRunning} onChange={e => setNeu(e.target.checked)} />
          auch schon Geprüfte neu (Eigenschaften, Texte)
        </label>
      </div>

      {job.isRunning && job.progress && (
        <div className="mt-2">
          <div className="h-1.5 rounded bg-[#DAD9D9]"><div className="h-1.5 rounded bg-[#1A1A1A]" style={{ width: `${job.progress.percentage}%` }} /></div>
          <p className="mt-1 truncate text-xs text-[#6B6A66]">{job.progress.message}</p>
        </div>
      )}
      {job.status === 'completed' && job.result && (
        <p className="mt-1 text-xs text-[#6B6A66]">
          {[
            job.result.bilder ? `${zahl(job.result.bilder.geprueft)} Farben geprüft` : null,
            job.result.eigenschaften ? `${zahl(job.result.eigenschaften.geprueft)} Eigenschaften` : null,
            job.result.texte ? `${zahl(job.result.texte.erzeugt)} Texte` : null,
          ].filter(Boolean).join(' · ') || 'Nichts offen'} · Kosten ≈ {usd(job.result.kostenUsd ?? 0)}
          {Array.isArray(job.result.fehler) && job.result.fehler.length > 0 && (
            <span className="block text-red-800">
              {job.result.fehler.join(' · ')} — noch einmal starten prüft nur das Offene.
            </span>
          )}
        </p>
      )}
      {(fehler || job.error || job.status === 'failed') && (
        <p className="mt-1 text-xs text-red-800">{fehler ?? job.error ?? job.job?.error ?? 'Fehlgeschlagen.'}</p>
      )}
    </div>
  );
}
